import { type Position, Direction, EdgeType } from "./types"
import { Tile } from "./tile"

const DIRECTION_OFFSETS: Record<Direction, { dr: number; dc: number }> = {
  [Direction.N]: { dr: -1, dc: 0 },
  [Direction.E]: { dr: 0, dc: 1 },
  [Direction.S]: { dr: 1, dc: 0 },
  [Direction.W]: { dr: 0, dc: -1 },
}

export function oppositeDirection(d: Direction): Direction {
  return ((d + 2) % 4) as Direction
}

export function directionBetween(from: Position, to: Position): Direction | null {
  const dr = to.row - from.row
  const dc = to.col - from.col
  if (dr === -1 && dc === 0) return Direction.N
  if (dr === 0 && dc === 1) return Direction.E
  if (dr === 1 && dc === 0) return Direction.S
  if (dr === 0 && dc === -1) return Direction.W
  return null
}

export class Grid {
  readonly size: number
  private cells: (Tile | null)[][]

  constructor(size: number) {
    this.size = size
    this.cells = Array.from({ length: size }, () => Array(size).fill(null))
  }

  inBounds(pos: Position): boolean {
    return pos.row >= 0 && pos.row < this.size && pos.col >= 0 && pos.col < this.size
  }

  get(pos: Position): Tile | null {
    if (!this.inBounds(pos)) return null
    return this.cells[pos.row][pos.col]
  }

  set(pos: Position, tile: Tile): void {
    this.cells[pos.row][pos.col] = tile
  }

  isEmpty(pos: Position): boolean {
    return this.inBounds(pos) && this.cells[pos.row][pos.col] === null
  }

  getNeighbor(pos: Position, dir: Direction): Position {
    const offset = DIRECTION_OFFSETS[dir]
    return { row: pos.row + offset.dr, col: pos.col + offset.dc }
  }

  getOccupiedNeighbors(pos: Position): Position[] {
    const result: Position[] = []
    for (const dir of [Direction.N, Direction.E, Direction.S, Direction.W]) {
      const neighbor = this.getNeighbor(pos, dir)
      if (this.inBounds(neighbor) && this.get(neighbor) !== null) {
        result.push(neighbor)
      }
    }
    return result
  }

  hasAdjacentTile(pos: Position): boolean {
    return this.getOccupiedNeighbors(pos).length > 0
  }

  canPlace(pos: Position): boolean {
    return this.isEmpty(pos) && this.hasAdjacentTile(pos)
  }

  getValidPlacements(): Position[] {
    const result: Position[] = []
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        const pos = { row, col }
        if (this.canPlace(pos)) result.push(pos)
      }
    }
    return result
  }

  /** Check if two adjacent tiles are LAND-connected on their shared edge */
  areLandConnected(a: Position, b: Position): boolean {
    const dir = directionBetween(a, b)
    if (dir === null) return false
    const tileA = this.get(a)
    const tileB = this.get(b)
    if (!tileA || !tileB) return false
    return (
      tileA.getEdge(dir) === EdgeType.Land &&
      tileB.getEdge(oppositeDirection(dir)) === EdgeType.Land
    )
  }

  /** Get all LAND-connected neighbors of a position */
  getLandConnectedNeighbors(pos: Position): Position[] {
    const result: Position[] = []
    for (const dir of [Direction.N, Direction.E, Direction.S, Direction.W]) {
      const neighbor = this.getNeighbor(pos, dir)
      if (this.inBounds(neighbor) && this.get(neighbor) && this.areLandConnected(pos, neighbor)) {
        result.push(neighbor)
      }
    }
    return result
  }

  /** Count all placed tiles */
  tileCount(): number {
    let count = 0
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        if (this.cells[row][col] !== null) count++
      }
    }
    return count
  }

  /** Iterate over all placed tiles */
  forEachTile(fn: (pos: Position, tile: Tile) => void): void {
    for (let row = 0; row < this.size; row++) {
      for (let col = 0; col < this.size; col++) {
        const tile = this.cells[row][col]
        if (tile) fn({ row, col }, tile)
      }
    }
  }
}
